import { tryCatch } from "@bcmi-labs/art-toolkit/utils"
import type { QueryKey, UseQueryOptions } from "@tanstack/react-query"

import { isDeterminateClientError } from "@/api/utils/NetworkError"
import { queryClient } from "@/lib/services/instances"

/**
 * Ensures that a query is in a "success" state and returns the data, or returns an error if the query failed.
 * This is useful for route guards and other cases where we need to ensure that the data is available before rendering.
 * Note that this will trigger a fetch if the query is not already in the cache, but it will not throw if the query is already in the cache with an error (it will just return the error).
 *
 * ⚠️ **IMPORTANT**: This function requires a concrete query type and cannot accept polymorphic queries.
 * Do NOT pass conditional queries (e.g., ternary expressions or mapped queries) as the argument, as TypeScript
 * cannot properly infer union types and the return type will break.
 *
 * @example
 * // ❌ BAD - TypeScript cannot infer the union type correctly:
 * const result = await ensureValidQueryData(
 *   condition ? apiClient.queryOptions(...) : otherClient.queryOptions(...)
 * )
 *
 * @example
 * // ✅ GOOD - Use conditional logic outside the function call:
 * const result = condition
 *   ? await ensureValidQueryData(apiClient.queryOptions(...))
 *   : await ensureValidQueryData(otherClient.queryOptions(...))
 *
 * // OR handle each case separately:
 * if (condition) {
 *   const result = await ensureValidQueryData(apiClient.queryOptions(...))
 * } else {
 *   const result = await ensureValidQueryData(otherClient.queryOptions(...))
 * }
 */
export async function ensureValidQueryData<TData = unknown, TError = unknown, TQueryKey extends QueryKey = QueryKey>(
  query: UseQueryOptions<TData, TError, TData, TQueryKey>
) {
  const result = await tryCatch(async () => {
    await queryClient.query(query)
    // ⚠️ `query` will throw on error, which means the query should be either in "success" or "error" state after this.
    const querystate = queryClient.getQueryState(query.queryKey)
    if (!querystate?.data || querystate.status !== "success") {
      throw new Error("Query failed")
    }
    return querystate.data as TData
  })

  // React Query keeps the last successful value when a refetch fails, so a
  // resource that is gone (or no longer ours, after a space switch or a revoked
  // share) would otherwise be served from cache for the rest of its
  // `staleTime`. Only a determinate client error evicts: a transient failure
  // must leave a good cached value alone, for every caller of this util.
  if (isDeterminateClientError(result.error)) {
    queryClient.removeQueries({ queryKey: query.queryKey, exact: true })
  }

  return result
}
